import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { storage } from '../utils/storage';
import { db } from '../config/firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage as firebaseStorage } from '../config/firebase';

interface EmpresaData {
  nomeEmpresa: string;
  email: string;
  telefone: string;
  whatsapp: string;
  cnpj: string;
  responsavel: string;
  endereco: {
    rua: string;
    numero: string;
    bairro: string;
  };
  complemento: string;
  pontoReferencia: string;
  segmento: string;
  coordenadas: {
    latitude: number;
    longitude: number;
  };
  horarios: {
    manha: boolean;
    almoco: boolean;
    tarde: boolean;
    noite: boolean;
  };
  foto: string;
}

const segmentosList = [
  { label: 'Lanchonete', value: 'lanchonete' },
  { label: 'Pizzaria', value: 'pizzaria' },
  { label: 'Açaiteria', value: 'acaiteria' },
  { label: 'Sorveteria', value: 'sorveteria' },
  { label: 'Restaurante', value: 'restaurante' },
  { label: 'Hamburgueria', value: 'hamburgueria' },
  { label: 'Padaria', value: 'padaria' },
  { label: 'Doceria', value: 'doceria' },
  { label: 'Farmácia', value: 'farmacia' },
  { label: 'Mercado', value: 'mercado' },
  { label: 'Loja de Peças', value: 'loja_pecas' },
  { label: 'Loja de Roupas', value: 'loja_roupas' },
  { label: 'Loja de Calçados', value: 'loja_calcados' },
  { label: 'Distribuidora de Bebidas', value: 'distribuidora_bebidas' },
  { label: 'Pet Shop', value: 'petshop' },
  { label: 'Outro', value: 'outro' }
];

const EmpresaPerfil: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [empresaId, setEmpresaId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  
  const [formData, setFormData] = useState<EmpresaData>({
    nomeEmpresa: '',
    email: '',
    telefone: '',
    whatsapp: '',
    cnpj: '',
    responsavel: '',
    endereco: {
      rua: '',
      numero: '',
      bairro: ''
    },
    complemento: '',
    pontoReferencia: '',
    segmento: '',
    coordenadas: {
      latitude: 0,
      longitude: 0
    },
    horarios: {
      manha: false,
      almoco: false,
      tarde: false,
      noite: false
    },
    foto: ''
  });

  const [tempHorarios, setTempHorarios] = useState(formData.horarios);
  const [showHorarioModal, setShowHorarioModal] = useState(false);
  const [showSegmentoModal, setShowSegmentoModal] = useState(false);

  useEffect(() => {
    carregarDadosEmpresa();
  }, []);

  const carregarDadosEmpresa = async () => {
    try {
      setIsLoading(true);
      const userId = await storage.getItem('userId');
      if (!userId) {
        alert('Usuário não identificado.');
        navigate('/login');
        return;
      }

      const userDoc = await getDoc(doc(db, 'usuarios', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const empresaId = userData.empresaId;
        
        if (empresaId) {
          setEmpresaId(empresaId);
          const empresaDoc = await getDoc(doc(db, 'Empresa', empresaId));
          if (empresaDoc.exists()) {
            const data = empresaDoc.data();
            
            setFormData({
              nomeEmpresa: data.nomeEmpresa || data.nome || '',
              email: data.email || '',
              telefone: data.telefone || '',
              whatsapp: data.whatsapp || '',
              cnpj: data.cnpj || '',
              responsavel: data.responsavel || '',
              endereco: {
                rua: data.endereco?.rua || '',
                numero: data.endereco?.numero || '',
                bairro: data.endereco?.bairro || ''
              },
              complemento: data.complemento || '',
              pontoReferencia: data.pontoReferencia || '',
              segmento: data.segmento || '',
              coordenadas: data.coordenadas || { latitude: 0, longitude: 0 },
              horarios: data.horarios || {
                manha: false,
                almoco: false,
                tarde: false,
                noite: false
              },
              foto: data.foto || data.fotoUrl || ''
            });
            
            setTempHorarios(data.horarios || {
              manha: false,
              almoco: false,
              tarde: false,
              noite: false
            });
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      alert('Não foi possível carregar os dados da empresa.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tamanho (máximo 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 10MB.');
      return;
    }

    // Validar tipo
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione uma imagem válida.');
      return;
    }

    try {
      setUploadingPhoto(true);
      const storageRef = ref(firebaseStorage, `fotos-empresa/${empresaId}_${Date.now()}.jpg`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      await updateDoc(doc(db, 'Empresa', empresaId), {
        foto: downloadURL,
        atualizadoEm: serverTimestamp()
      });

      setFormData(prev => ({ ...prev, foto: downloadURL }));
      alert('Foto da empresa atualizada com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer upload da foto:', error);
      alert('Não foi possível fazer upload da foto.');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSalvar = async () => {
    try {
      setIsLoading(true);
      
      await updateDoc(doc(db, 'Empresa', empresaId), {
        nomeEmpresa: formData.nomeEmpresa,
        email: formData.email,
        telefone: formData.telefone,
        whatsapp: formData.whatsapp,
        cnpj: formData.cnpj,
        responsavel: formData.responsavel,
        endereco: {
          rua: formData.endereco.rua,
          numero: formData.endereco.numero,
          bairro: formData.endereco.bairro
        },
        complemento: formData.complemento,
        pontoReferencia: formData.pontoReferencia,
        segmento: formData.segmento,
        coordenadas: {
          latitude: formData.coordenadas.latitude,
          longitude: formData.coordenadas.longitude
        },
        horarios: formData.horarios,
        atualizadoEm: serverTimestamp()
      });
      
      setIsEditing(false);
      alert('Dados atualizados com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Não foi possível salvar as alterações.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSalvarHorarios = async () => {
    try {
      await updateDoc(doc(db, 'Empresa', empresaId), {
        horarios: tempHorarios,
        atualizadoEm: serverTimestamp()
      });
      setFormData(prev => ({ ...prev, horarios: tempHorarios }));
      setShowHorarioModal(false);
      alert('Horários atualizados com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar horários:', error);
      alert('Não foi possível salvar os horários.');
    }
  };

  const getSegmentoLabel = (value: string) => {
    const segmento = segmentosList.find(s => s.value === value);
    return segmento ? segmento.label : (value || 'Não definido');
  };

  const getHorariosAtivos = () => {
    const ativos = [];
    if (formData.horarios.manha) ativos.push('Manhã');
    if (formData.horarios.almoco) ativos.push('Almoço');
    if (formData.horarios.tarde) ativos.push('Tarde');
    if (formData.horarios.noite) ativos.push('Noite');
    return ativos.length > 0 ? ativos.join(', ') : 'Não definido';
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (isLoading && !formData.nomeEmpresa) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando perfil...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200/50 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowMenu(true)}
                className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Perfil da Empresa</h1>
                <p className="text-sm text-gray-500">Gerencie suas informações</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/empresa')}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-2 px-4 py-2 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <span>←</span>
              <span>Voltar</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-8">
        {/* Banner de modo de edição */}
        {isEditing && (
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-xl mb-6 flex items-center gap-3 shadow-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span className="font-medium">Modo de Edição Ativo - Os campos estão disponíveis para edição</span>
          </div>
        )}

        {/* Foto da Empresa */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Foto da Empresa</h2>
            {isEditing && (
              <button
                onClick={handleFileSelect}
                disabled={uploadingPhoto}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {uploadingPhoto ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Enviando...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Alterar Foto</span>
                  </>
                )}
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
          <div className="flex justify-center">
            {formData.foto ? (
              <img
                src={formData.foto}
                alt={formData.nomeEmpresa}
                className="w-32 h-32 rounded-2xl object-cover border-4 border-gray-200 shadow-lg"
              />
            ) : (
              <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center border-4 border-gray-200 shadow-lg">
                <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Card de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-lg p-4 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-600">Segmento</p>
            <p className="text-lg font-bold text-gray-900">{getSegmentoLabel(formData.segmento)}</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 text-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-2">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-600">Bairro</p>
            <p className="text-lg font-bold text-gray-900">{formData.endereco.bairro || '--'}</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-600">Horários</p>
            <p className="text-sm font-bold text-gray-900">{getHorariosAtivos()}</p>
          </div>
        </div>

        {/* Informações Básicas */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Informações Básicas</h2>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span>Editar</span>
              </button>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nome da Empresa *</label>
              <input
                type="text"
                value={formData.nomeEmpresa}
                onChange={(e) => setFormData(prev => ({ ...prev, nomeEmpresa: e.target.value }))}
                disabled={!isEditing}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Nome da empresa"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">CNPJ</label>
              <input
                type="text"
                value={formData.cnpj}
                onChange={(e) => setFormData(prev => ({ ...prev, cnpj: e.target.value }))}
                disabled={!isEditing}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="CNPJ"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Responsável</label>
              <input
                type="text"
                value={formData.responsavel}
                onChange={(e) => setFormData(prev => ({ ...prev, responsavel: e.target.value }))}
                disabled={!isEditing}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Nome do responsável"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Segmento</label>
              {isEditing ? (
                <select
                  value={formData.segmento}
                  onChange={(e) => setFormData(prev => ({ ...prev, segmento: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                >
                  <option value="">Selecione um segmento</option>
                  {segmentosList.map(seg => (
                    <option key={seg.value} value={seg.value}>{seg.label}</option>
                  ))}
                </select>
              ) : (
                <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-900">
                  {getSegmentoLabel(formData.segmento)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contato */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Contato</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">E-mail</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                disabled={!isEditing}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Telefone</label>
              <input
                type="tel"
                value={formData.telefone}
                onChange={(e) => setFormData(prev => ({ ...prev, telefone: e.target.value }))}
                disabled={!isEditing}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Telefone"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp</label>
              <input
                type="tel"
                value={formData.whatsapp}
                onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value }))}
                disabled={!isEditing}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="WhatsApp"
              />
            </div>
          </div>
        </div>

        {/* Endereço */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Endereço</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rua</label>
                <input
                  type="text"
                  value={formData.endereco.rua}
                  onChange={(e) => setFormData(prev => ({ ...prev, endereco: { ...prev.endereco, rua: e.target.value } }))}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Rua/Avenida"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Número</label>
                <input
                  type="text"
                  value={formData.endereco.numero}
                  onChange={(e) => setFormData(prev => ({ ...prev, endereco: { ...prev.endereco, numero: e.target.value } }))}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Número"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bairro</label>
              <input
                type="text"
                value={formData.endereco.bairro}
                onChange={(e) => setFormData(prev => ({ ...prev, endereco: { ...prev.endereco, bairro: e.target.value } }))}
                disabled={!isEditing}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Bairro"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Complemento</label>
              <input
                type="text"
                value={formData.complemento}
                onChange={(e) => setFormData(prev => ({ ...prev, complemento: e.target.value }))}
                disabled={!isEditing}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Complemento (opcional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ponto de Referência</label>
              <input
                type="text"
                value={formData.pontoReferencia}
                onChange={(e) => setFormData(prev => ({ ...prev, pontoReferencia: e.target.value }))}
                disabled={!isEditing}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Ponto de referência"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Latitude</label>
                <input
                  type="number"
                  step="any"
                  value={formData.coordenadas.latitude || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, coordenadas: { ...prev.coordenadas, latitude: parseFloat(e.target.value) || 0 } }))}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Ex: -15.5989"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
                <input
                  type="number"
                  step="any"
                  value={formData.coordenadas.longitude || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, coordenadas: { ...prev.coordenadas, longitude: parseFloat(e.target.value) || 0 } }))}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Ex: -56.0949"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Horários de Funcionamento */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Horário de Funcionamento</h2>
            <button
              onClick={() => {
                setTempHorarios(formData.horarios);
                setShowHorarioModal(true);
              }}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Editar Horários</span>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className={`p-4 rounded-xl border-2 ${formData.horarios.manha ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <span className="font-semibold text-gray-900">Manhã</span>
              </div>
              <p className="text-sm text-gray-600">07:00-11:00</p>
              <p className={`text-xs font-medium mt-1 ${formData.horarios.manha ? 'text-green-600' : 'text-gray-500'}`}>
                {formData.horarios.manha ? 'Ativo' : 'Inativo'}
              </p>
            </div>

            <div className={`p-4 rounded-xl border-2 ${formData.horarios.almoco ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="font-semibold text-gray-900">Almoço</span>
              </div>
              <p className="text-sm text-gray-600">11:00-14:00</p>
              <p className={`text-xs font-medium mt-1 ${formData.horarios.almoco ? 'text-green-600' : 'text-gray-500'}`}>
                {formData.horarios.almoco ? 'Ativo' : 'Inativo'}
              </p>
            </div>

            <div className={`p-4 rounded-xl border-2 ${formData.horarios.tarde ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <span className="font-semibold text-gray-900">Tarde</span>
              </div>
              <p className="text-sm text-gray-600">13:00-18:00</p>
              <p className={`text-xs font-medium mt-1 ${formData.horarios.tarde ? 'text-green-600' : 'text-gray-500'}`}>
                {formData.horarios.tarde ? 'Ativo' : 'Inativo'}
              </p>
            </div>

            <div className={`p-4 rounded-xl border-2 ${formData.horarios.noite ? 'border-purple-300 bg-purple-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
                <span className="font-semibold text-gray-900">Noite</span>
              </div>
              <p className="text-sm text-gray-600">18:00-23:00</p>
              <p className={`text-xs font-medium mt-1 ${formData.horarios.noite ? 'text-green-600' : 'text-gray-500'}`}>
                {formData.horarios.noite ? 'Ativo' : 'Inativo'}
              </p>
            </div>
          </div>
        </div>

        {/* Botões de Ação */}
        {isEditing && (
          <div className="flex gap-4">
            <button
              onClick={() => {
                setIsEditing(false);
                carregarDadosEmpresa(); // Recarrega os dados originais
              }}
              className="flex-1 px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
            >
              Cancelar
            </button>
            <button
              onClick={handleSalvar}
              disabled={isLoading}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Salvando...
                </span>
              ) : (
                'Salvar Alterações'
              )}
            </button>
          </div>
        )}
      </main>

      {/* Modal de Horários */}
      {showHorarioModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Configurar Horários</h2>
                <button
                  onClick={() => setShowHorarioModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-xl border-2 border-yellow-200">
                <div>
                  <p className="font-semibold text-gray-900">Manhã</p>
                  <p className="text-sm text-gray-600">07:00-11:00</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tempHorarios.manha}
                    onChange={(e) => setTempHorarios(prev => ({ ...prev, manha: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-orange-50 rounded-xl border-2 border-orange-200">
                <div>
                  <p className="font-semibold text-gray-900">Almoço</p>
                  <p className="text-sm text-gray-600">11:00-14:00</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tempHorarios.almoco}
                    onChange={(e) => setTempHorarios(prev => ({ ...prev, almoco: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl border-2 border-red-200">
                <div>
                  <p className="font-semibold text-gray-900">Tarde</p>
                  <p className="text-sm text-gray-600">13:00-18:00</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tempHorarios.tarde}
                    onChange={(e) => setTempHorarios(prev => ({ ...prev, tarde: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border-2 border-purple-200">
                <div>
                  <p className="font-semibold text-gray-900">Noite</p>
                  <p className="text-sm text-gray-600">18:00-23:00</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tempHorarios.noite}
                    onChange={(e) => setTempHorarios(prev => ({ ...prev, noite: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowHorarioModal(false)}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleSalvarHorarios}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Menu Lateral */}
      {showMenu && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
            onClick={() => setShowMenu(false)}
          />
          <div className="fixed inset-y-0 left-0 w-80 bg-white/95 backdrop-blur-xl shadow-2xl z-50 overflow-y-auto border-r border-gray-200/50 transform transition-transform">
            <div className="p-6 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Menu</h2>
                <button
                  onClick={() => setShowMenu(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex flex-col items-center">
                {formData.foto ? (
                  <img
                    src={formData.foto}
                    alt={formData.nomeEmpresa}
                    className="w-20 h-20 rounded-2xl border-4 border-white/30 shadow-xl mb-3 object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm border-4 border-white/30 shadow-xl mb-3 flex items-center justify-center">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                )}
                <p className="text-lg font-bold">{formData.nomeEmpresa || 'Empresa'}</p>
                <p className="text-sm text-blue-100 mt-1">Painel Administrativo</p>
              </div>
            </div>

            <nav className="p-4 space-y-2">
              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/empresa');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Dashboard</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/status-pedido');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Pedidos</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/perfil');
                }}
                className="w-full text-left px-4 py-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <span className="font-semibold text-blue-700">Perfil</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/clientes');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Lista de Clientes</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/relatorio');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Histórico de Entregas</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/tabela-taxas');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Tabela de Taxas</span>
              </button>

              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate('/configuracoes');
                }}
                className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-700">Configurações</span>
              </button>

              <div className="pt-4 mt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowMenu(false);
                    handleLogout();
                  }}
                  className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all flex items-center gap-4 group"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </div>
                  <span className="font-semibold text-red-600">Sair</span>
                </button>
              </div>
            </nav>
          </div>
        </>
      )}
    </div>
  );
};

export default EmpresaPerfil;

